import { Link } from '@tanstack/react-router'
import type {
  InvoiceDetailResponseDto,
  InvoicePdfVersionResponseDto,
  PaymentResponseDto,
} from '@trellis/api'
import { DetailCard } from '@trellis/roots/data'
import { IconBadge } from '@trellis/roots/media'
import type { IconBadgeTone } from '@trellis/roots/media'
import { Typography } from '@trellis/roots/typography'
import {
  Activity,
  Ban,
  Check,
  FileText,
  Flag,
  Mail,
  Plus,
  RefreshCw,
  Send,
  XCircle,
} from 'lucide-react'
import type { LucideIcon } from 'lucide-react'

import { env } from '@/env'
import { formatDate } from '@/lib/format'
import { cn } from '@/lib/utils'

const IS_NON_PROD = env.VITE_DEPLOY_ENV !== 'prod'

interface ActivityEvent {
  type:
    | 'create'
    | 'send'
    | 'pay'
    | 'flag'
    | 'cancel'
    | 'void'
    | 'reminder-email'
    | 'pdf'
    | 'reissue'
  label: string
  linkedInvoiceId?: string
  detail?: string
  when: string
}

interface PdfVersionSummary {
  versionNumber: number
  generatedAt: string
  generatedBy: { name: string }
}

function describeReminderEmail(
  reminderEmail: InvoiceDetailResponseDto['reminderEmails'][number],
): Pick<ActivityEvent, 'label' | 'detail'> {
  const divertedNote = IS_NON_PROD ? 'diverted to a test inbox' : null
  if (reminderEmail.source === 'SCHEDULED') {
    return {
      label: `Scheduled reminder sent to ${reminderEmail.sentTo}`,
      detail: divertedNote ?? undefined,
    }
  }
  const senderName =
    reminderEmail.sentByName ?? (reminderEmail.sentByUserId ? 'a removed user' : null)
  const sender = senderName ? `by ${senderName} (manual)` : 'manual'
  return {
    label: `Reminder email sent to ${reminderEmail.sentTo}`,
    detail: [sender, divertedNote].filter(Boolean).join(' · '),
  }
}

function buildActivity(
  invoice: InvoiceDetailResponseDto,
  payments: PaymentResponseDto[],
  reminderEmails: InvoiceDetailResponseDto['reminderEmails'],
  pdfVersions: PdfVersionSummary[],
): ActivityEvent[] {
  const events: ActivityEvent[] = [
    { type: 'create', label: 'Invoice created', when: invoice.createdAt },
  ]
  if (invoice.sentAt) {
    events.push({ type: 'send', label: 'Sent to client', when: invoice.sentAt })
  }
  for (const reminderEmail of reminderEmails) {
    events.push({
      type: 'reminder-email',
      ...describeReminderEmail(reminderEmail),
      when: reminderEmail.sentAt,
    })
  }
  for (const pdfVersion of pdfVersions) {
    events.push({
      type: 'pdf',
      label: `PDF generated (v${pdfVersion.versionNumber}) by ${pdfVersion.generatedBy.name}`,
      when: pdfVersion.generatedAt,
    })
  }
  if (invoice.status === 'OVERDUE') {
    events.push({
      type: 'flag',
      label: 'Marked overdue',
      when: invoice.dueAt ?? invoice.updatedAt,
    })
  }
  if (invoice.status === 'PAID' && invoice.paidAt) {
    const manualPayment = payments.find(
      (payment) => payment.method === 'MANUAL' && payment.status === 'SUCCEEDED',
    )
    events.push(
      manualPayment?.recordedByName
        ? {
            type: 'pay',
            label: `Payment recorded by ${manualPayment.recordedByName}`,
            when: manualPayment.paidAt,
          }
        : { type: 'pay', label: 'Payment received', when: invoice.paidAt },
    )
  }
  if (invoice.status === 'CANCELLED') {
    events.push({ type: 'cancel', label: 'Cancelled', when: invoice.updatedAt })
  }
  if (invoice.status === 'VOID') {
    events.push({ type: 'void', label: 'Voided', when: invoice.updatedAt })
  }
  if (invoice.reissuedAs) {
    events.push({
      type: 'reissue',
      label: `Re-issued as ${invoice.reissuedAs.invoiceNumber}`,
      linkedInvoiceId: invoice.reissuedAs.id,
      when: invoice.reissuedAs.linkedAt,
    })
  }
  if (invoice.reissuedFrom) {
    events.push({
      type: 'reissue',
      label: `Re-issued from ${invoice.reissuedFrom.invoiceNumber}`,
      linkedInvoiceId: invoice.reissuedFrom.id,
      when: invoice.reissuedFrom.linkedAt,
    })
  }
  return events.sort((a, b) => new Date(b.when).getTime() - new Date(a.when).getTime())
}

const TIMELINE_ICON_MAP: Record<
  ActivityEvent['type'],
  { icon: LucideIcon; tone: IconBadgeTone }
> = {
  create: { icon: Plus, tone: 'neutral' },
  send: { icon: Send, tone: 'blue' },
  pay: { icon: Check, tone: 'green' },
  flag: { icon: Flag, tone: 'red' },
  cancel: { icon: XCircle, tone: 'rose' },
  void: { icon: Ban, tone: 'neutral' },
  'reminder-email': { icon: Mail, tone: 'sky' },
  pdf: { icon: FileText, tone: 'violet' },
  reissue: { icon: RefreshCw, tone: 'amber' },
}

function TimelineRow({ event, isLast }: { event: ActivityEvent; isLast: boolean }) {
  const { icon, tone } = TIMELINE_ICON_MAP[event.type]
  return (
    <div className={cn('flex items-start gap-2.5 py-2.5', !isLast && 'border-b')}>
      <IconBadge icon={icon} tone={tone} />
      <div className="min-w-0 flex-1">
        <Typography variant="body-sm" className="font-medium">
          {event.linkedInvoiceId ? (
            <Link
              to="/invoices/$id"
              params={{ id: event.linkedInvoiceId }}
              className="underline-offset-2 hover:underline"
            >
              {event.label}
            </Link>
          ) : (
            event.label
          )}
        </Typography>
        <Typography variant="label" color="muted" className="tabular-nums">
          {formatDate(event.when)}
          {event.detail && ` · ${event.detail}`}
        </Typography>
      </div>
    </div>
  )
}

export interface InvoiceActivityTimelineProps {
  invoice: InvoiceDetailResponseDto
  payments: PaymentResponseDto[]
  pdfVersions: InvoicePdfVersionResponseDto[]
}

export function InvoiceActivityTimeline({
  invoice,
  payments,
  pdfVersions,
}: InvoiceActivityTimelineProps) {
  const activityEvents = buildActivity(
    invoice,
    payments,
    invoice.reminderEmails,
    pdfVersions,
  )

  return (
    <DetailCard
      title="Activity"
      icon={Activity}
      description="Timeline of changes"
      contentClassName="px-5 py-2"
    >
      {activityEvents.map((activityEvent, eventIdx) => (
        <TimelineRow
          key={eventIdx}
          event={activityEvent}
          isLast={eventIdx === activityEvents.length - 1}
        />
      ))}
    </DetailCard>
  )
}
